import { createElement, Fragment } from 'react';
import cn from 'classnames';
import { useTranslate } from '@awardit/react-use-translate';
import { objectWithoutPropertiesLoose as _objectWithoutPropertiesLoose, extends as _extends, createForOfIteratorHelperLoose as _createForOfIteratorHelperLoose } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Button } from '../Button/index.js';
import { Wrapper } from '../Wrapper/index.js';
import { Filterbar } from '../Filterbar/index.js';
import { OffCanvasFilterMenu } from '../OffCanvasFilterMenu/index.js';
import { OffCanvasSortMenu } from '../OffCanvasSortMenu/index.js';
import { ProductList } from '../ProductList/index.js';
import { StickBelowHeader } from '../StickBelowHeader/index.js';
import { useFilter } from '../UseFilter/index.js';
import { PaginatedProductList } from '../PaginatedProductList/index.js';
import styles from 'ui-components/FilterableProductList.scss';

var _excluded = ["children", "updating", "productList", "load", "exponentiation", "nonLinearSlider", "filterIcon", "sortIcon", "uiContext", "ProductCardComponent", "usePagination", "breadcrumbLinks", "category", "numPages", "productsPerRow", "title", "pointsPriceId", "usePoints", "sortButtonSide", "priceLock", "addFilterBarOffset"];
var useGetSortValues = function useGetSortValues(productList) {
  var BLACKLISTED_SORT_VALUES = new Set(["position"]);
  var ONLY_DESC = new Set([]);
  var t = useTranslate();
  if (!productList.sortableBy) {
    return [];
  }
  var sortableBy = productList.sortableBy.filter(function (sb) {
    return !BLACKLISTED_SORT_VALUES.has(sb.code);
  });
  var sortValues = [{
    code: "",
    label: t("FILTER.FILTERBAR.SORT_DEFAULT")
  }];
  for (var _iterator = _createForOfIteratorHelperLoose(sortableBy), _step; !(_step = _iterator()).done;) {
    var value = _step.value;
    if (!ONLY_DESC.has(value.code)) {
      sortValues.push({
        code: value.code + "_asc",
        label: t("FILTER.FILTERBAR.SORT_" + value.code.toUpperCase() + "_UP")
      });
    }
    sortValues.push({
      code: value.code + "_desc",
      label: t("FILTER.FILTERBAR.SORT_" + value.code.toUpperCase() + "_DOWN")
    });
  }
  return sortValues;
};
var FilterableProductListInner = function FilterableProductListInner(_ref) {
  var _cn;
  var children = _ref.children,
    updating = _ref.updating,
    productList = _ref.productList,
    load = _ref.load,
    sortValues = _ref.sortValues,
    exponentiation = _ref.exponentiation,
    nonLinearSlider = _ref.nonLinearSlider,
    filterIcon = _ref.filterIcon,
    sortIcon = _ref.sortIcon,
    uiContext = _ref.uiContext,
    _ref$usePoints = _ref.usePoints,
    usePoints = _ref$usePoints === void 0 ? false : _ref$usePoints,
    sortButtonSide = _ref.sortButtonSide,
    priceLock = _ref.priceLock,
    addFilterBarOffset = _ref.addFilterBarOffset;
  var t = useTranslate();
  var _uiContext = uiContext(),
    filterOCMOpen = _uiContext.filterOCMOpen,
    setFilterOCMOpen = _uiContext.setFilterOCMOpen,
    sortOCMOpen = _uiContext.sortOCMOpen,
    setSortOCMOpen = _uiContext.setSortOCMOpen;
  var filterConfig = {
    loading: updating,
    productList: productList,
    usePoints: usePoints,
    load: load,
    sortValues: sortValues
  };
  var filterState = useFilter(filterConfig);
  var FilterButton = function FilterButton() {
    var _ref2;
    return /*#__PURE__*/createElement(Button, {
      "aria-expanded": (_ref2 = filterOCMOpen === true) != null ? _ref2 : "true",
      "aria-controls": "filterMenu",
      className: styles.filterButton,
      size: "medium",
      variant: "ghost",
      slotLeft: filterIcon,
      onClick: function onClick() {
        return setFilterOCMOpen(true);
      }
    }, t("FILTER.FILTERBAR.ALL_FILTERS"));
  };
  var SortButton = function SortButton() {
    var _ref3;
    return /*#__PURE__*/createElement(Button, {
      "aria-expanded": (_ref3 = sortOCMOpen === true) != null ? _ref3 : "true",
      "aria-controls": "sortMenu",
      className: styles.sortButton,
      size: "medium",
      variant: "ghost",
      slotLeft: sortIcon,
      onClick: function onClick() {
        return setSortOCMOpen(true);
      }
    }, t("FILTER.FILTERBAR.SORT"));
  };
  return /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement(StickBelowHeader, {
    className: cn(styles.stickyWrapper, (_cn = {}, _cn[styles.filterbarOffset] = addFilterBarOffset, _cn)),
    render: function render(fixed) {
      var _cn2, _cn3;
      return sortValues && sortValues.length > 0 ? /*#__PURE__*/createElement(Filterbar, {
        className: cn((_cn2 = {}, _cn2[styles.filterbarFixed] = fixed, _cn2)),
        Button: FilterButton,
        SortButton: SortButton,
        filterState: filterState,
        nonLinearSlider: nonLinearSlider,
        exponentiation: exponentiation,
        openFilters: function openFilters() {
          return setFilterOCMOpen(true);
        },
        sortButtonSide: sortButtonSide,
        hidePriceFilters: priceLock
      }) : /*#__PURE__*/createElement(Filterbar, {
        className: cn((_cn3 = {}, _cn3[styles.filterbarFixed] = fixed, _cn3)),
        Button: FilterButton,
        filterState: filterState,
        nonLinearSlider: nonLinearSlider,
        exponentiation: exponentiation,
        openFilters: function openFilters() {
          return setFilterOCMOpen(true);
        },
        hidePriceFilters: priceLock
      });
    }
  }), /*#__PURE__*/createElement(OffCanvasFilterMenu, {
    id: "filterMenu",
    isOpen: filterOCMOpen,
    close: function close() {
      return setFilterOCMOpen(false);
    },
    nonLinearSlider: nonLinearSlider,
    exponentiation: exponentiation,
    filterState: filterState
  }), /*#__PURE__*/createElement(OffCanvasSortMenu, {
    id: "sortMenu",
    isOpen: sortOCMOpen,
    close: function close() {
      return setSortOCMOpen(false);
    },
    filterState: filterState,
    side: "RIGHT"
  }), !filterState.visible && /*#__PURE__*/createElement("div", {
    className: styles.spacer
  }), children);
};
var FilterableProductList = function FilterableProductList(_ref4) {
  var children = _ref4.children,
    updating = _ref4.updating,
    productList = _ref4.productList,
    load = _ref4.load,
    exponentiation = _ref4.exponentiation,
    _ref4$nonLinearSlider = _ref4.nonLinearSlider,
    nonLinearSlider = _ref4$nonLinearSlider === void 0 ? true : _ref4$nonLinearSlider,
    filterIcon = _ref4.filterIcon,
    sortIcon = _ref4.sortIcon,
    uiContext = _ref4.uiContext,
    ProductCardComponent = _ref4.ProductCardComponent,
    _ref4$usePagination = _ref4.usePagination,
    usePagination = _ref4$usePagination === void 0 ? true : _ref4$usePagination,
    breadcrumbLinks = _ref4.breadcrumbLinks,
    category = _ref4.category,
    _ref4$numPages = _ref4.numPages,
    numPages = _ref4$numPages === void 0 ? 1 : _ref4$numPages,
    productsPerRow = _ref4.productsPerRow,
    title = _ref4.title,
    pointsPriceId = _ref4.pointsPriceId,
    usePoints = _ref4.usePoints,
    sortButtonSide = _ref4.sortButtonSide,
    priceLock = _ref4.priceLock,
    addFilterBarOffset = _ref4.addFilterBarOffset,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded);
  var sortValues = useGetSortValues(productList);
  var categoryTitle = category ? category : sortValues.length > 0 && !category ? sortValues[0].label : "";
  return /*#__PURE__*/createElement(FilterableProductListInner, {
    productList: productList,
    sortValues: sortValues,
    updating: updating,
    load: load,
    exponentiation: exponentiation,
    nonLinearSlider: nonLinearSlider,
    filterIcon: filterIcon,
    sortIcon: sortIcon,
    uiContext: uiContext,
    usePoints: usePoints,
    sortButtonSide: sortButtonSide,
    priceLock: priceLock,
    addFilterBarOffset: addFilterBarOffset
  }, /*#__PURE__*/createElement(Wrapper, null, usePagination ? /*#__PURE__*/createElement(PaginatedProductList, _extends({}, rest, {
    productList: productList,
    numPages: numPages,
    uiContext: uiContext,
    updating: updating,
    title: title,
    productsPerRow: productsPerRow,
    category: categoryTitle,
    ProductCardComponent: ProductCardComponent,
    breadcrumbLinks: breadcrumbLinks,
    pointsPriceId: pointsPriceId
  })) : /*#__PURE__*/createElement(ProductList, _extends({}, rest, {
    products: productList.items,
    loading: updating,
    heading: title,
    productsPerRow: productsPerRow,
    category: categoryTitle,
    ProductCardComponent: ProductCardComponent,
    breadcrumbLinks: breadcrumbLinks,
    pointsPriceId: pointsPriceId
  })), children));
};
var FilterableProductListHint = function FilterableProductListHint(_ref5) {
  var children = _ref5.children;
  return /*#__PURE__*/createElement(Wrapper, null, /*#__PURE__*/createElement(StickBelowHeader, {
    className: styles.filterbar
  }, /*#__PURE__*/createElement("div", {
    className: styles.filterBarHint
  })), children, /*#__PURE__*/createElement(ProductList, {
    products: [null, null, null, null, null, null, null, null]
  }));
};

export { FilterableProductList, FilterableProductListHint };
//# sourceMappingURL=index.js.map
